import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Alert,
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Progress,
	Select,
	Table,
	Tag,
	Upload,
	message
} from "antd";
import type { TableColumnsType } from "antd";
import { FileUp, Plus } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";

import { useGetCodeBookImportsQuery } from "graphql/queries/get-code-book-imports.generated";
import { useGetFoundationalCodeBooksQuery } from "graphql/queries/get-foundational-code-books.generated";
import { useStartCodeBookImportMutation } from "graphql/mutations/start-code-book-import.generated";

import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { uploadToStorage } from "utils/files-management";
import { CodeBookType } from "constants/field-values";
import routeNames from "constants/route-names";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type ImportRow = NonNullable<ReturnType<typeof useGetCodeBookImportsQuery>["data"]>["code_book_imports"][number];

// The colour tells the user whether the row needs their attention: amber for REVIEW
// (waiting for a person), blue while it works, green when done, red on failure.
const STATUS_COLOR: Record<string, string> = {
	UPLOADED: "default",
	READING: "processing",
	REVIEW: "warning",
	PUBLISHING: "processing",
	PUBLISHED: "success",
	FAILED: "error"
};

const STATUS_LABEL: Record<string, string> = {
	UPLOADED: "Uploaded",
	READING: "Reading",
	REVIEW: "Ready to review",
	PUBLISHING: "Publishing",
	PUBLISHED: "Published",
	FAILED: "Failed"
};

/** "IFC 2021", or just "IFC" when the code has no edition year. */
const editionLabel = (book: { abbreviation: string; edition_year?: number | null }) =>
	`${book.abbreviation}${book.edition_year ? ` ${book.edition_year}` : ""}`;

/**
 * List of code book imports, and the form that starts a new one.
 *
 * This screen replaces the old manual process: converting the PDF on a developer machine,
 * packing it into a tarball with a manifest, uploading it to S3, and running a migration
 * through an SSM shell into the running task.
 *
 * The PDF is uploaded from the browser directly to Firebase Storage, so a 60 MB code book
 * never passes through the action's request body. Only the resulting URL is sent to the
 * server, which then reads the file in the background. This list shows the status of each
 * run.
 */
const PlanReviewCodeImports = () => {
	// Tells Ollie what the reader is looking at.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The code book imports screen: the list of runs that loaded a code book into the platform so its sections can be cited by a rule."
		})
	);

	const navigate = useNavigate();
	const { jurisdictionId, picker } = usePlanReviewJurisdiction();
	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();

	const [form] = Form.useForm();
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [selectedPdf, setSelectedPdf] = useState<File | null>(null);

	const {
		data: importsData,
		loading: isLoadingImports,
		refetch: refetchImports
	} = useGetCodeBookImportsQuery({ fetchPolicy: "cache-and-network" });

	// Used to fill the "Amends" dropdown: a local amendment declares which national
	// edition it rewrites, so that a rule citing the national section also reaches the
	// local text.
	//
	// This is not scoped to the authority chosen at the top of the screen. A national
	// code has no jurisdiction of its own, because every authority shares the same
	// edition, so the dropdown has to be filled whether or not an authority is chosen.
	const { data: nationalCodesData } = useGetFoundationalCodeBooksQuery();
	const [startCodeBookImport] = useStartCodeBookImportMutation();

	const selectedBookType = Form.useWatch("book_type", form);

	const nationalCodeOptions = useMemo(
		() =>
			(nationalCodesData?.code_books ?? []).map(book => ({
				value: book.id,
				label: editionLabel(book)
			})),
		[nationalCodesData]
	);

	const submitNewImport = useCallback(async () => {
		if (!canManage) return blockChange();
		const formValues = await form.validateFields();
		if (!selectedPdf) {
			message.error("Choose the PDF of the code first.");
			return;
		}

		try {
			// Step 1: upload the PDF to Storage. `setUploadProgress` drives the progress
			// bar; a null value means no upload is in progress.
			setUploadProgress(0);
			const { publicUrl } = await uploadToStorage(
				selectedPdf,
				setUploadProgress,
				`code-books/${Date.now()}-${selectedPdf.name}`
			);

			// Step 2: create the import row. The server answers immediately and reads the
			// PDF in the background.
			const { data: startResult } = await startCodeBookImport({
				variables: {
					input: {
						abbreviation: formValues.abbreviation.trim(),
						edition_year: formValues.edition_year ?? null,
						title: formValues.title.trim(),
						publisher: formValues.publisher || null,
						book_type: formValues.book_type,
						// A local amendment belongs to the jurisdiction currently selected;
						// a national code belongs to no one in particular.
						jurisdiction_id: formValues.book_type === CodeBookType.AMENDMENT ? jurisdictionId : null,
						amends_code_book_id: formValues.amends_code_book_id ?? null,
						source_file_name: selectedPdf.name,
						source_file_url: publicUrl
					}
				}
			});

			setIsFormOpen(false);
			form.resetFields();
			setSelectedPdf(null);
			await refetchImports();

			// Go straight to the detail screen, which follows the reading as it happens.
			const importId = startResult?.start_code_book_import?.import_id;
			if (importId) navigate(`${routeNames.PLAN_REVIEW_CODE_IMPORTS}/${importId}`);
		} catch (error: any) {
			// The server validates the input and returns a message the user can act on:
			// for example, the same document is already on file under a different
			// abbreviation, or the amended edition has not been imported yet.
			message.error(error?.graphQLErrors?.[0]?.message ?? error?.message ?? "The import could not be started.");
		} finally {
			setUploadProgress(null);
		}
	}, [blockChange, canManage, form, jurisdictionId, navigate, refetchImports, selectedPdf, startCodeBookImport]);

	const columns: TableColumnsType<ImportRow> = [
		{
			title: "Code",
			key: "code",
			render: (_, row) => (
				<div className="prci-code">
					<strong>{editionLabel(row)}</strong>
					<span>{row.title}</span>
					{row.jurisdiction_record ? <em>{row.jurisdiction_record.name}</em> : null}
				</div>
			)
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string, row) => (
				<div className="prci-status">
					<Tag color={STATUS_COLOR[status]}>{STATUS_LABEL[status] ?? status}</Tag>
					{row.error_message ? <span title={row.error_message}>!</span> : null}
				</div>
			)
		},
		{
			title: "Pages",
			dataIndex: "page_count",
			key: "page_count",
			render: (pages: number | null) => pages ?? "—"
		},
		{
			title: "Provisions",
			key: "sections",
			render: (_, row) => (row.section_count == null ? "—" : row.section_count)
		},
		{
			// The measure to watch: lines that the page prints in the shape of a provision
			// but that did not become a row. The missing-parents count only ever detects a
			// provision that has children of its own, so it can miss whole losses.
			title: "Not captured",
			dataIndex: "not_captured",
			key: "not_captured",
			render: (value: number | null) =>
				value == null ? "—" : <span className={value ? "prci-warn" : "prci-clean"}>{value}</span>
		},
		{
			title: "Commentary",
			dataIndex: "commentary_family",
			key: "commentary_family",
			render: (family: string | null) => family ?? "none"
		}
	];

	return (
		<OllieLayout>
			<div className="prci-page">
				<div className="prci-header">
					<div>
						<h1>Code Imports</h1>
						<p>
							Upload a published code and extract the provisions that a rule can cite. Nothing is written to the codes
							in use until you publish it.
						</p>
					</div>

					<button className="prci-btn prci-btn-primary" disabled={!canManage} onClick={() => setIsFormOpen(true)}>
						<Plus size={16} />
						Add a code
					</button>
					<div className="prci-header-actions">{picker}</div>
				</div>
				{notice}

				<Table
					className="prci-table"
					rowKey="id"
					loading={isLoadingImports}
					columns={columns}
					dataSource={importsData?.code_book_imports ?? []}
					onRow={row => ({
						onClick: () => navigate(`${routeNames.PLAN_REVIEW_CODE_IMPORTS}/${row.id}`)
					})}
					pagination={{ pageSize: 20, hideOnSinglePage: true }}
				/>

				<Modal
					title="Add a code"
					open={isFormOpen}
					onCancel={() => setIsFormOpen(false)}
					onOk={submitNewImport}
					okText="Read it"
					confirmLoading={uploadProgress !== null}
					destroyOnClose
				>
					<Form form={form} layout="vertical" initialValues={{ book_type: CodeBookType.FOUNDATIONAL }}>
						<Form.Item
							name="abbreviation"
							label="Abbreviation"
							rules={[{ required: true, message: "A rule cites the code by this." }]}
							extra="How a citation refers to this code: IFC, IBC, NFPA 13. A standard includes its number here, because NFPA 13 and NFPA 13R are different documents, not two editions of the same one."
						>
							<Input placeholder="IFC" />
						</Form.Item>

						<Form.Item name="edition_year" label="Edition">
							<InputNumber min={1900} max={2100} placeholder="2021" />
						</Form.Item>

						<Form.Item
							name="title"
							label="Title"
							rules={[{ required: true, message: "The title as the code prints it." }]}
						>
							<Input placeholder="2021 International Fire Code" />
						</Form.Item>

						<Form.Item name="publisher" label="Publisher">
							<Input placeholder="International Code Council" />
						</Form.Item>

						<Form.Item name="book_type" label="Kind">
							<Select
								options={[
									{
										value: CodeBookType.FOUNDATIONAL,
										label: "National code — shared by every authority that adopts it"
									},
									{
										value: CodeBookType.AMENDMENT,
										label: "Local amendments — only this authority enforces them"
									}
								]}
							/>
						</Form.Item>

						{selectedBookType === CodeBookType.AMENDMENT ? (
							<>
								{/* Amendments belong to one authority, and the authority comes from
								    the picker at the top of the screen, not from this form. Saying
								    so here saves uploading the PDF before the server refuses it. */}
								{!jurisdictionId ? (
									<Alert
										type="warning"
										showIcon
										className="prci-form-alert"
										message="Choose the authority first"
										description="Amendments belong to one authority. Close this form, pick it in the Jurisdiction box at the top of the screen, and open the form again."
									/>
								) : null}
								<Form.Item
									name="amends_code_book_id"
									label="Amends"
									extra="Which national edition these amendments rewrite. A rule that cites that edition then also reaches the local text, without citing it separately."
								>
									<Select
										allowClear
										showSearch
										optionFilterProp="label"
										options={nationalCodeOptions}
										notFoundContent="No national code is published yet. Import one first."
									/>
								</Form.Item>
							</>
						) : null}

						<Form.Item label="The PDF" required>
							<Upload.Dragger
								accept="application/pdf"
								maxCount={1}
								// Returning false stops antd from uploading the file itself: the
								// upload happens in submitNewImport, so it can report progress.
								beforeUpload={selectedFile => {
									setSelectedPdf(selectedFile);
									return false;
								}}
								onRemove={() => setSelectedPdf(null)}
							>
								<p className="prci-upload-icon">
									<FileUp size={28} />
								</p>
								<p>Drop the code here, or click to choose it.</p>
								<p className="prci-upload-hint">
									The file is uploaded from your browser straight to storage, so a 60 MB code works the same as a small
									one.
								</p>
							</Upload.Dragger>
							{uploadProgress !== null ? <Progress percent={uploadProgress} /> : null}
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</OllieLayout>
	);
};

export default PlanReviewCodeImports;
